import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, type SmsMessage } from "../lib/api";
import { useToast } from "./toast-context";

export function SmsInbox({
  token,
  isAdmin,
}: {
  token: string;
  isAdmin: boolean;
}) {
  const [phone, setPhone] = useState("");
  const [activePhone, setActivePhone] = useState("");
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [optedOut, setOptedOut] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState("");
  const [reviewed, setReviewed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sendError, setSendError] = useState("");
  const [pendingKey, setPendingKey] = useState("");
  const busy = useRef(false);
  const phoneRef = useRef<HTMLInputElement>(null);
  const announce = useToast();

  useEffect(() => {
    const guard = (event: BeforeUnloadEvent) => {
      if (message || pendingKey) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [message, pendingKey]);

  async function load(target: string) {
    setLoading(true);
    setError("");
    try {
      const response = await api.smsConversation(token, target);
      setMessages(response.messages);
      setOptedOut(response.optedOut);
      setTruncated(response.truncated);
      setActivePhone(target);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Conversation unavailable.",
      );
    } finally {
      setLoading(false);
    }
  }
  function open(event: FormEvent) {
    event.preventDefault();
    if (!/^\+[1-9]\d{7,14}$/.test(phone.trim())) {
      setError(
        "Enter a phone number with its country code, for example +12025550123.",
      );
      phoneRef.current?.focus();
      return;
    }
    void load(phone.trim());
  }
  async function send(event: FormEvent) {
    event.preventDefault();
    if (
      busy.current ||
      !message.trim() ||
      !consent.trim() ||
      !reviewed ||
      optedOut
    )
      return;
    busy.current = true;
    setSending(true);
    setSendError("");
    const key = pendingKey || crypto.randomUUID();
    setPendingKey(key);
    try {
      await api.sendSms(
        token,
        {
          to: activePhone,
          message: message.trim(),
          consentReference: consent.trim(),
        },
        key,
      );
      setPendingKey("");
      setMessage("");
      setReviewed(false);
      announce(
        "Reply accepted by the messaging service. Delivery is not yet confirmed.",
        "success",
      );
      await load(activePhone);
    } catch (reason) {
      // These responses are returned before a send reservation exists.
      if (
        reason instanceof Error &&
        "status" in reason &&
        [400, 401, 403, 422].includes(Number(reason.status))
      )
        setPendingKey("");
      setSendError(
        reason instanceof Error
          ? reason.message
          : "The send outcome is uncertain. Retry this same reply.",
      );
    } finally {
      busy.current = false;
      setSending(false);
    }
  }
  if (!isAdmin)
    return (
      <section className="panel">
        <h2>SMS access requires admin authority</h2>
        <p>Ask your SideKick administrator for access.</p>
        <a href="#command">Return to Overview</a>
      </section>
    );
  return (
    <section className="panel sms-workspace" aria-labelledby="sms-heading">
      <div className="section-heading">
        <div>
          <p className="eyebrow">CONVERSATIONS</p>
          <h2 id="sms-heading">Text with your network</h2>
        </div>
      </div>
      <p className="fine-print">
        Read incoming texts and reply from SideKick’s dedicated number. Replies
        are written by an admin.
      </p>
      <form className="intake-form sms-open" noValidate onSubmit={open}>
        <label>
          Recipient phone number
          <input
            ref={phoneRef}
            type="tel"
            autoComplete="off"
            value={phone}
            disabled={
              loading || sending || Boolean(message) || Boolean(pendingKey)
            }
            onChange={(event) => setPhone(event.target.value)}
            placeholder="+12025550123"
            aria-describedby="sms-phone-help"
          />
        </label>
        <p id="sms-phone-help" className="fine-print">
          Include the country code. Phone numbers stay out of the page URL.
        </p>
        <button
          className="button button--outline"
          disabled={
            loading || sending || Boolean(pendingKey) || Boolean(message)
          }
        >
          {loading ? "Loading…" : "Open conversation"}
        </button>
      </form>
      {error && (
        <p className="alert alert--error" role="alert">
          {error}
        </p>
      )}
      {activePhone && (
        <>
          <div className="section-heading">
            <h3>{activePhone}</h3>
            <button
              type="button"
              className="button button--outline"
              disabled={loading || sending}
              onClick={() => void load(activePhone)}
            >
              Refresh texts
            </button>
          </div>
          {optedOut && (
            <p className="alert alert--error" role="status">
              This recipient has opted out. Replies are blocked.
            </p>
          )}
          {truncated && (
            <p className="fine-print">Showing the most recent 100 texts.</p>
          )}
          <ol
            className="sms-thread"
            aria-label="Conversation, newest first"
            aria-busy={loading}
          >
            {messages.map((item) => (
              <li
                key={item.id}
                className={`sms-message sms-message--${item.direction.toLowerCase()}`}
              >
                <div>
                  <strong>
                    {item.direction === "INBOUND"
                      ? "Received"
                      : "SideKick reply"}
                  </strong>
                  <small>
                    {new Date(item.receivedAt).toLocaleString("en-US")} ·{" "}
                    {item.status === "PENDING"
                      ? "Outcome unconfirmed"
                      : item.status === "ACCEPTED"
                        ? "Accepted by service"
                        : "Received"}
                  </small>
                </div>
                <p>{item.message}</p>
              </li>
            ))}
          </ol>
          {!messages.length && !loading && (
            <p className="center-state">No texts in this conversation yet.</p>
          )}
          <form
            className="intake-form"
            noValidate
            onSubmit={(event) => void send(event)}
            aria-busy={sending}
          >
            <label>
              Reply
              <textarea
                className="resize-none"
                rows={4}
                maxLength={160}
                value={message}
                disabled={sending || optedOut || Boolean(pendingKey)}
                onChange={(event) => {
                  setMessage(event.target.value);
                  setReviewed(false);
                }}
                aria-describedby="sms-reply-help"
              />
            </label>
            <p id="sms-reply-help" className="fine-print">
              {message.length}/160 characters. Avoid sensitive information.
              Accepted texts may still fail to reach the phone.
            </p>
            <label>
              Consent record reference
              <input
                value={consent}
                maxLength={200}
                disabled={sending || Boolean(pendingKey)}
                onChange={(event) => {
                  setConsent(event.target.value);
                  setReviewed(false);
                }}
              />
            </label>
            <label className="sms-review">
              <input
                type="checkbox"
                checked={reviewed}
                disabled={sending || optedOut || Boolean(pendingKey)}
                onChange={(event) => setReviewed(event.target.checked)}
              />
              I reviewed this reply and confirm the recipient consented to
              receive it.
            </label>
            {sendError && (
              <div className="alert alert--error" role="alert">
                <p>{sendError}</p>
                <p>
                  {pendingKey
                    ? `Keep this conversation open. Retry uses the same request to prevent duplicate sending. Request key: ${pendingKey}`
                    : "Correct the issue before submitting again."}
                </p>
              </div>
            )}
            <div className="form-actions">
              <button
                className="button button--primary"
                disabled={
                  sending ||
                  loading ||
                  optedOut ||
                  !activePhone ||
                  !message.trim() ||
                  !consent.trim() ||
                  !reviewed
                }
              >
                {sending
                  ? "Sending…"
                  : pendingKey
                    ? "Retry same reply"
                    : "Send reply"}
              </button>
            </div>
          </form>
        </>
      )}
    </section>
  );
}
